import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Coffee, LogOut, Menu as MenuIcon, Moon, Plus, Search, Sun, UserRound, Contact, Handshake, CheckSquare, Users, PlayCircle } from 'lucide-react';
import { formatClock } from '@stoplord/shared';
import { useAuth } from '@/store/auth';
import { toast, useUi } from '@/store/ui';
import { useCreate } from '@/store/create';
import { useCan, useTerms } from '@/hooks/usePermission';
import { authApi } from '@/services/auth';
import { notificationsApi } from '@/services/admin';
import { workApi } from '@/services/hrm';
import { errorMessage } from '@/lib/api';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Avatar, Button } from '@/components/ui';
import { Menu, MenuItem } from '@/components/Menu';

export function WorkSessionPill({ mobile = false }: { mobile?: boolean }) {
  const ws = useAuth((s) => s.workSession);
  const setWs = useAuth((s) => s.setWorkSession);
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const breakMut = useMutation({
    mutationFn: () => (ws?.state === 'on_break' ? workApi.endBreak() : workApi.startBreak()),
    onSuccess: (s) => setWs(s),
    onError: (e) => toast.error('Could not update break', errorMessage(e)),
  });
  if (!ws || ws.state === 'ended') return null;
  // Active time is server-accounted; between heartbeats we extrapolate only while active.
  const sinceBeat = ws.state === 'active' ? Math.min(Date.now() - new Date(ws.lastHeartbeatAt).getTime(), 90_000) : 0;
  const active = ws.activeMs + Math.max(0, sinceBeat);
  const label = ws.state === 'on_break' ? 'On break' : ws.state === 'idle' ? 'Idle' : 'Working';
  const dot = ws.state === 'on_break' ? 'bg-info' : ws.state === 'idle' ? 'bg-warning' : 'bg-success';
  return (
    <div className={cn('items-center gap-1 rounded-full border border-line bg-surface-muted py-1 pl-3 pr-1', mobile ? 'flex w-full justify-between' : 'hidden sm:flex')} aria-live="off">
      <span className="flex items-center gap-1.5">
      <span className={cn('h-2 w-2 rounded-full', dot)} aria-hidden />
      <span className="text-xs font-medium text-ink-muted">{label}</span>
      <span className="tabular ml-1 text-xs font-semibold text-ink" title={`Since ${new Date(ws.loginAt).toLocaleTimeString()} · idle ${Math.round(ws.idleMs / 60000)}m · break ${Math.round(ws.breakMs / 60000)}m`}>{formatClock(active)}</span>
      </span>
      <button
        type="button"
        onClick={() => breakMut.mutate()}
        disabled={breakMut.isPending}
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-surface px-2 py-1 text-xs font-medium text-ink-muted ring-1 ring-line hover:text-ink"
      >
        {ws.state === 'on_break' ? <><PlayCircle className="h-3.5 w-3.5" /> Resume</> : <><Coffee className="h-3.5 w-3.5" /> Break</>}
      </button>
    </div>
  );
}

function Notifications() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const unread = useQuery({ queryKey: ['notifications', 'unread'], queryFn: notificationsApi.unread, refetchInterval: 120_000 });
  const list = useQuery({ queryKey: ['notifications', 'latest'], queryFn: () => notificationsApi.list({ limit: 8 }) });
  const readAll = useMutation({ mutationFn: notificationsApi.readAll, onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) });
  const count = unread.data?.count ?? 0;
  return (
    <Menu
      className="w-[min(92vw,360px)]"
      trigger={({ toggle }) => (
        <button type="button" onClick={toggle} className="relative rounded-full p-2 text-ink-muted ring-1 ring-line hover:text-ink" aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}>
          <Bell className="h-4 w-4" />
          {count > 0 && <span className="tabular absolute -right-1 -top-1 min-w-[18px] rounded-full bg-danger px-1 text-center text-[10px] font-semibold leading-[18px] text-white">{count > 99 ? '99+' : count}</span>}
        </button>
      )}
    >
      {(close) => (
        <div>
          <div className="flex items-center justify-between px-3 py-2">
            <p className="text-sm font-semibold">Notifications</p>
            {count > 0 && <button type="button" className="text-xs text-brand-ink hover:underline" onClick={() => readAll.mutate()}>Mark all read</button>}
          </div>
          <ul className="max-h-80 overflow-y-auto border-t border-line">
            {list.data?.items.length === 0 && <li className="px-3 py-6 text-center text-sm text-ink-subtle">You’re all caught up.</li>}
            {list.data?.items.map((n) => (
              <li key={n._id}>
                <button
                  type="button"
                  onClick={async () => {
                    close();
                    if (!n.readAt) await notificationsApi.read(n._id).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }));
                    if (n.link) navigate(n.link);
                  }}
                  className="flex w-full gap-2 px-3 py-2.5 text-left hover:bg-surface-hover"
                >
                  <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-brand')} aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-ink">{n.title}</span>
                    {n.body && <span className="line-clamp-2 block text-xs text-ink-muted">{n.body}</span>}
                    <span className="block text-[11px] text-ink-subtle">{relativeTime(n.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => { close(); navigate('/notifications'); }} className="block w-full border-t border-line px-3 py-2 text-center text-xs font-medium text-brand-ink hover:bg-surface-hover">View all</button>
        </div>
      )}
    </Menu>
  );
}

function QuickCreate() {
  const can = useCan();
  const terms = useTerms();
  const show = useCreate((s) => s.show);
  const items = [
    can('crm.leads.create') && { label: `Add ${terms.lead.singular.toLowerCase()}`, icon: <Contact className="h-4 w-4" />, kind: 'lead' as const },
    can('crm.clients.create') && { label: `Add ${terms.client.singular.toLowerCase()}`, icon: <Handshake className="h-4 w-4" />, kind: 'client' as const },
    can('tasks.create') && { label: `Create ${terms.task.singular.toLowerCase()}`, icon: <CheckSquare className="h-4 w-4" />, kind: 'task' as const },
    can('hrm.employees.create') && { label: `Add ${terms.employee.singular.toLowerCase()}`, icon: <Users className="h-4 w-4" />, kind: 'employee' as const },
  ].filter(Boolean) as { label: string; icon: React.ReactNode; kind: 'lead' | 'client' | 'task' | 'employee' }[];
  if (!items.length) return null;
  return (
    <Menu trigger={({ toggle }) => <Button size="sm" onClick={toggle} icon={<Plus className="h-4 w-4" />} aria-label="Quick create"><span className="hidden sm:inline">New</span></Button>}>
      {(close) => items.map((it) => <MenuItem key={it.kind} icon={it.icon} onClick={() => { close(); show(it.kind); }}>{it.label}</MenuItem>)}
    </Menu>
  );
}

export function Topbar() {
  const user = useAuth((s) => s.user);
  const { theme, toggleTheme, setMobileNav, setCommandOpen } = useUi();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-line bg-surface/90 px-4 backdrop-blur sm:gap-3 sm:px-6">
      <button type="button" onClick={() => setMobileNav(true)} className="rounded-lg p-2 text-ink-muted hover:bg-surface-hover lg:hidden" aria-label="Open navigation"><MenuIcon className="h-5 w-5" /></button>
      <button type="button" onClick={() => setCommandOpen(true)} className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-line bg-surface-muted px-3 text-left text-sm text-ink-subtle hover:border-line-strong sm:max-w-md">
        <Search className="h-4 w-4 shrink-0" />
        <span className="truncate">Search leads, clients, tasks, people…</span>
        <kbd className="ml-auto hidden rounded border border-line px-1.5 text-[10px] text-ink-subtle sm:inline">Ctrl K</kbd>
      </button>
      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        <WorkSessionPill />
        <QuickCreate />
        <Notifications />
        <button type="button" onClick={toggleTheme} className="hidden rounded-full p-2 text-ink-muted ring-1 ring-line hover:text-ink sm:block" aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <Menu
          trigger={({ toggle }) => (
            <button type="button" onClick={toggle} className="flex items-center gap-2 rounded-full sm:border-l sm:border-line sm:pl-3" aria-label="Account menu">
              <Avatar name={user?.name} />
              <span className="hidden text-left md:block">
                <span className="block max-w-[140px] truncate text-sm font-medium text-ink">{user?.name}</span>
                <span className="block max-w-[140px] truncate text-xs text-ink-subtle">{user?.roles.map((r) => r.name).join(', ')}</span>
              </span>
            </button>
          )}
        >
          {(close) => (
            <>
              <div className="border-b border-line px-3 py-2">
                <p className="truncate text-sm font-medium">{user?.name}</p>
                <p className="truncate text-xs text-ink-subtle">{user?.email}</p>
              </div>
              <MenuItem icon={<UserRound className="h-4 w-4" />} onClick={() => { close(); navigate('/profile'); }}>Profile & security</MenuItem>
              <MenuItem icon={theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} onClick={() => { toggleTheme(); close(); }}>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</MenuItem>
              <MenuItem danger icon={<LogOut className="h-4 w-4" />} onClick={() => { close(); void authApi.logout(); }}>Sign out</MenuItem>
            </>
          )}
        </Menu>
      </div>
    </header>
  );
}
